/**
 * PartnerActivate (V2.0.63) — full 3-step activation wizard.
 *
 * Replaces the V2.0.62 bridge page. Partners can now self-serve their
 * activation instead of waiting for our team's 15-min call:
 *   Step 1  Set password       POST /partner/activation/{id}/password
 *   Step 2  KYC (PAN + upload) POST /partner/activation/{id}/kyc
 *   Step 3  GST + Banking      POST /partner/activation/{id}/gst
 *
 * The row_id in the URL is the capability token (matches the deep
 * link from the batch-invite email). Backend refuses steps until the
 * row has `invited_at` set, so a random URL guess is a dead end.
 *
 * Progress bar + step gating — you can revisit an earlier step but
 * can't skip forward until the current one is submitted successfully.
 * The `activation_steps` array on the row lets us resume: if a
 * partner comes back later, we start them on the first incomplete
 * step.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    CheckCircle,
    LockKey,
    IdentificationCard,
    Bank,
    CaretRight,
    Circle,
    Upload,
} from "@phosphor-icons/react";

import api from "@/lib/api";

const STEPS = [
    { key: "password", label: "Set password", Icon: LockKey },
    { key: "kyc", label: "KYC (PAN)", Icon: IdentificationCard },
    { key: "gst", label: "GST + Banking", Icon: Bank },
];

export default function PartnerActivate() {
    const { rowId } = useParams();
    const navigate = useNavigate();
    const [row, setRow] = useState(null);
    const [error, setError] = useState(null);
    const [currentStep, setCurrentStep] = useState("password");

    useEffect(() => {
        if (!rowId) { setError("Missing activation code"); return; }
        api.get(`/partner/activation/${rowId}`)
            .then((r) => {
                setRow(r.data);
                if (!r.data.invited_at) {
                    setError("This partner hasn't been invited yet. Ping the NaukriDaddy team.");
                    return;
                }
                if (r.data.activated_at) {
                    setError("This account is already activated. You can sign in normally.");
                    return;
                }
                // Resume at the first incomplete step.
                const done = r.data.activation_steps || [];
                const next = STEPS.find((s) => !done.includes(s.key));
                setCurrentStep(next ? next.key : "password");
            })
            .catch((e) => setError(e?.response?.status === 404
                ? "This activation link is invalid or expired."
                : "Couldn't load your invite."));
    }, [rowId]);

    const onStepComplete = (stepKey, updatedSteps, activated) => {
        setRow((r) => ({ ...r, activation_steps: updatedSteps, activated_at: activated ? new Date().toISOString() : r?.activated_at }));
        if (activated) {
            toast.success("Activation complete — welcome aboard!");
            return;
        }
        const idx = STEPS.findIndex((s) => s.key === stepKey);
        const next = STEPS[idx + 1];
        if (next) setCurrentStep(next.key);
    };

    if (error) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-16" data-testid="partner-activate-error">
                <div className="max-w-md w-full bg-white border border-[#E5E5E5] p-8 text-center">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Partner activation</p>
                    <h1 className="font-heading text-2xl font-black tracking-tight text-[#0A0A0A] mb-3">Can't open activation</h1>
                    <p className="text-sm text-[#525252] mb-6">{error}</p>
                    <Link to="/partner" className="bg-[#0A0A0A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-block">Back to partner page</Link>
                </div>
            </div>
        );
    }

    if (!row) {
        return <div className="min-h-screen bg-cream flex items-center justify-center"><p className="text-sm text-[#A3A3A3]">Loading…</p></div>;
    }

    const done = row.activation_steps || [];
    const activated = !!row.activated_at || done.length >= 3;

    return (
        <div className="min-h-screen bg-cream py-10 sm:py-16 px-4" data-testid="partner-activate-wizard">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-2">NaukriDaddy Partner activation</p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]">
                        Welcome, {(row.name || "partner").split(" ")[0]}.
                    </h1>
                    <p className="text-sm text-[#525252] mt-2">Three quick steps and you're live on the requirement pool. Takes about 5 minutes.</p>
                </div>

                {/* Progress bar */}
                <ol className="flex items-center gap-2 mb-8 overflow-x-auto" data-testid="wizard-progress">
                    {STEPS.map((s, idx) => {
                        const stepDone = done.includes(s.key);
                        const isCurrent = currentStep === s.key && !stepDone;
                        return (
                            <React.Fragment key={s.key}>
                                <li
                                    className={`flex items-center gap-2 px-3 py-2 border ${
                                        stepDone ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                                        : isCurrent ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                                        : "border-[#E5E5E5] bg-white text-[#A3A3A3]"
                                    }`}
                                    data-testid={`wizard-step-${s.key}${stepDone ? "-done" : ""}`}
                                >
                                    {stepDone ? <CheckCircle size={14} weight="fill" /> : <s.Icon size={14} weight="bold" />}
                                    <span className="text-[11px] font-black uppercase tracking-wider">
                                        {idx + 1}. {s.label}
                                    </span>
                                </li>
                                {idx < STEPS.length - 1 && <CaretRight size={12} weight="bold" className="text-[#A3A3A3] shrink-0" />}
                            </React.Fragment>
                        );
                    })}
                </ol>

                {/* Success card once everything is done */}
                {activated ? (
                    <SuccessCard row={row} />
                ) : (
                    <div className="bg-white border border-[#E5E5E5] p-6 sm:p-8">
                        {currentStep === "password" && <PasswordStep rowId={rowId} onDone={(s) => onStepComplete("password", s)} />}
                        {currentStep === "kyc" && <KycStep rowId={rowId} onDone={(s) => onStepComplete("kyc", s)} />}
                        {currentStep === "gst" && <GstStep rowId={rowId} defaults={row} onDone={(s, activated) => onStepComplete("gst", s, activated)} />}
                    </div>
                )}
            </div>
        </div>
    );
}


/* ============================ STEP 1 ============================ */
function PasswordStep({ rowId, onDone }) {
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) return toast.error("Password must be at least 8 characters");
        if (pw !== pw2) return toast.error("Passwords don't match");
        setSubmitting(true);
        try {
            const r = await api.post(`/partner/activation/${rowId}/password`, { password: pw });
            toast.success("Password set");
            onDone(r.data.steps_completed);
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Couldn't set password");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="wizard-form-password">
            <h2 className="font-heading text-2xl font-black tracking-tight">Step 1 · Set your password</h2>
            <p className="text-sm text-[#525252]">You'll use this + your email to sign into the partner portal when it opens.</p>
            <input
                type="password" placeholder="New password (min 8 chars)"
                value={pw} onChange={(e) => setPw(e.target.value)}
                required minLength={8}
                data-testid="wizard-input-password"
                className="w-full border border-[#E5E5E5] px-3 py-2.5"
            />
            <input
                type="password" placeholder="Confirm password"
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                required minLength={8}
                data-testid="wizard-input-password2"
                className="w-full border border-[#E5E5E5] px-3 py-2.5"
            />
            <button
                type="submit" disabled={submitting}
                data-testid="wizard-submit-password"
                className="bg-[#0A0A0A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-40"
            >
                {submitting ? "Saving…" : "Save password"} <CaretRight size={12} weight="bold" />
            </button>
        </form>
    );
}


/* ============================ STEP 2 ============================ */
function KycStep({ rowId, onDone }) {
    const [pan, setPan] = useState("");
    const [file, setFile] = useState(null);
    const [uploadId, setUploadId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);

    const upload = async (f) => {
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            // Reuse the CV upload endpoint — it accepts any PDF/image
            // and lands it in pending_uploads for the backend to
            // consume when the KYC step submits.
            const r = await api.post("/public/resumes/parse", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUploadId(r.data.upload_id);
            toast.success("PAN uploaded");
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Upload failed");
            setFile(null);
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        const clean = pan.trim().toUpperCase();
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(clean)) return toast.error("PAN format: ABCDE1234F");
        setSubmitting(true);
        try {
            const r = await api.post(`/partner/activation/${rowId}/kyc`, {
                pan_number: clean, pan_upload_id: uploadId,
            });
            toast.success("KYC saved");
            onDone(r.data.steps_completed);
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "KYC save failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="wizard-form-kyc">
            <h2 className="font-heading text-2xl font-black tracking-tight">Step 2 · KYC verification</h2>
            <p className="text-sm text-[#525252]">Required for GST invoicing + payout compliance.</p>
            <input
                type="text" placeholder="PAN number (e.g. ABCDE1234F)"
                value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())}
                required maxLength={10}
                data-testid="wizard-input-pan"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 uppercase"
            />
            <div>
                <label className="flex items-center gap-2 border border-dashed border-[#E5E5E5] px-4 py-3 cursor-pointer hover:bg-[#FAFAFA]">
                    <Upload size={14} weight="bold" />
                    <span className="text-xs font-bold uppercase tracking-wider">
                        {file ? file.name : "Upload PAN card (JPG/PDF, ≤ 5 MB)"}
                    </span>
                    <input
                        type="file" accept="image/*,application/pdf" hidden
                        data-testid="wizard-input-pan-file"
                        onChange={(e) => { setFile(e.target.files?.[0] || null); upload(e.target.files?.[0]); }}
                    />
                </label>
                {uploading && <p className="text-[11px] text-[#A3A3A3] mt-1">Uploading…</p>}
                {uploadId && <p className="text-[11px] text-emerald-700 mt-1">✓ Uploaded — will attach to your KYC record</p>}
            </div>
            <button
                type="submit" disabled={submitting || uploading}
                data-testid="wizard-submit-kyc"
                className="bg-[#0A0A0A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-40"
            >
                {submitting ? "Saving…" : "Save KYC"} <CaretRight size={12} weight="bold" />
            </button>
        </form>
    );
}


/* ============================ STEP 3 ============================ */
function GstStep({ rowId, defaults, onDone }) {
    const [f, setF] = useState({
        business_name: defaults?.name || "",
        gstin: "",
        address_line1: "",
        city: defaults?.city || "",
        state: "",
        pincode: "",
        bank_account_name: "",
        bank_account_number: "",
        bank_ifsc: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...f, gstin: f.gstin.trim().toUpperCase() || null, bank_ifsc: f.bank_ifsc.trim().toUpperCase() };
            const r = await api.post(`/partner/activation/${rowId}/gst`, payload);
            toast.success("Activation complete!");
            onDone(r.data.steps_completed, r.data.activated);
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Couldn't save GST info");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-3" data-testid="wizard-form-gst">
            <h2 className="font-heading text-2xl font-black tracking-tight">Step 3 · GST + Banking</h2>
            <p className="text-sm text-[#525252]">This is what will appear on your placement invoice. GSTIN is optional if you don't have one yet.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required placeholder="Business / trading name" value={f.business_name} onChange={set("business_name")} data-testid="wizard-input-biz-name" className="border border-[#E5E5E5] px-3 py-2.5" />
                <input placeholder="GSTIN (optional)" value={f.gstin} onChange={set("gstin")} maxLength={15} data-testid="wizard-input-gstin" className="border border-[#E5E5E5] px-3 py-2.5 uppercase" />
                <input required placeholder="Address line 1" value={f.address_line1} onChange={set("address_line1")} data-testid="wizard-input-address" className="border border-[#E5E5E5] px-3 py-2.5 sm:col-span-2" />
                <input required placeholder="City" value={f.city} onChange={set("city")} data-testid="wizard-input-city" className="border border-[#E5E5E5] px-3 py-2.5" />
                <input required placeholder="State" value={f.state} onChange={set("state")} data-testid="wizard-input-state" className="border border-[#E5E5E5] px-3 py-2.5" />
                <input required placeholder="Pincode (6 digits)" value={f.pincode} onChange={set("pincode")} maxLength={6} data-testid="wizard-input-pincode" className="border border-[#E5E5E5] px-3 py-2.5" />
            </div>
            <div className="pt-3 border-t border-[#F5F5F5] grid grid-cols-1 sm:grid-cols-2 gap-3">
                <p className="text-xs font-black uppercase tracking-wider text-[#525252] sm:col-span-2">Bank account for payouts</p>
                <input required placeholder="Account holder name" value={f.bank_account_name} onChange={set("bank_account_name")} data-testid="wizard-input-bank-name" className="border border-[#E5E5E5] px-3 py-2.5 sm:col-span-2" />
                <input required placeholder="Account number" value={f.bank_account_number} onChange={set("bank_account_number")} data-testid="wizard-input-bank-acc" className="border border-[#E5E5E5] px-3 py-2.5" />
                <input required placeholder="IFSC (e.g. HDFC0001234)" value={f.bank_ifsc} onChange={set("bank_ifsc")} maxLength={11} data-testid="wizard-input-ifsc" className="border border-[#E5E5E5] px-3 py-2.5 uppercase" />
            </div>
            <button
                type="submit" disabled={submitting}
                data-testid="wizard-submit-gst"
                className="bg-emerald-600 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-40"
            >
                {submitting ? "Activating…" : "Complete activation"} <CheckCircle size={12} weight="bold" />
            </button>
        </form>
    );
}


/* ============================ SUCCESS ============================ */
function SuccessCard({ row }) {
    return (
        <div className="bg-white border border-emerald-200 p-8 sm:p-10" data-testid="wizard-success">
            <CheckCircle size={40} weight="fill" className="text-emerald-600 mb-4" />
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-800 mb-2">Activation complete</p>
            <h2 className="font-heading text-3xl font-black tracking-tight mb-3">
                You're live on the requirement pool, {(row.name || "partner").split(" ")[0]}.
            </h2>
            <p className="text-sm text-[#525252] leading-relaxed mb-5">
                We'll email you within 24 hours with your first curated batch of open roles matching your city and function.
                GST invoicing is enabled — placement fees release after the 60/90-day guarantee window closes.
            </p>
            <Link to="/partner" data-testid="wizard-success-back" className="bg-[#0A0A0A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-block">
                Back to partner page
            </Link>
        </div>
    );
}
